import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { supabaseSecure, USE_SECURE_COOKIES } from "@/lib/secureClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { z } from "zod";
import { ThemeToggle } from "@/components/ThemeToggle";
import { logAuditEvent } from "@/lib/auditLog";
import { useRateLimitedAuth } from "@/hooks/useRateLimitedAuth";
import { AlertTriangle } from "lucide-react";

// Use the appropriate client based on environment
const client = USE_SECURE_COOKIES ? supabaseSecure : supabase;

const emailSchema = z.string().email("Invalid email address");
const passwordSchema = z
  .string()
  .min(10, "Password must be at least 10 characters")
  .regex(/[a-z]/, "Password must contain lowercase letters")
  .regex(/[A-Z]/, "Password must contain uppercase letters")
  .regex(/[0-9]/, "Password must contain numbers")
  .regex(/[^a-zA-Z0-9]/, "Password must contain special characters");
const nameSchema = z
  .string()
  .min(1, "Name is required")
  .max(100, "Name must be less than 100 characters")
  .regex(/^[a-zA-Z\s\-']+$/, "Name can only contain letters, spaces, hyphens, and apostrophes");

const Auth = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockoutTimeRemaining, setLockoutTimeRemaining] = useState<number | null>(null);
  const navigate = useNavigate();
  const { signIn, signUp, rateLimitState, isRateLimited } = useRateLimitedAuth();

  // Update lockout countdown
  useEffect(() => {
    if (!rateLimitState.resetTime || !isRateLimited) {
      setLockoutTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((rateLimitState.resetTime! - Date.now()) / 1000));
      if (remaining <= 0) {
        setLockoutTimeRemaining(null);
      } else {
        setLockoutTimeRemaining(remaining);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [rateLimitState.resetTime, isRateLimited]);

  const validateInputs = () => {
    try {
      emailSchema.parse(email);
      if (isSignUp) {
        passwordSchema.parse(password);
        nameSchema.parse(name.trim());
      } else {
        // For login, only require non-empty password
        if (!password) {
          toast.error("Password is required");
          return false;
        }
      }
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.issues[0].message);
      }
      return false;
    }
  };

  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateInputs()) return;

    if (isRateLimited && lockoutTimeRemaining && lockoutTimeRemaining > 0) {
      toast.error(`Please wait ${formatTimeRemaining(lockoutTimeRemaining)} before trying again.`);
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        const result = await signUp(email, password, name.trim());
        
        if (!result.success) {
          if (result.retryAfter) {
            toast.error(`Too many attempts. Please wait ${Math.ceil(result.retryAfter / 60)} minutes.`);
          } else {
            toast.error(result.error || "Signup failed");
          }
          return;
        }

        if (result.user) {
          await logAuditEvent({
            action: "User signed up",
            category: "authentication",
            entityType: "user",
            entityId: result.user.id,
            metadata: { email },
          });
          toast.success("Account created! Please wait for admin approval before logging in.");
          setIsSignUp(false);
        }
      } else {
        const result = await signIn(email, password);
        
        if (!result.success) {
          if (result.retryAfter) {
            toast.error(`Too many attempts. Please wait ${Math.ceil(result.retryAfter / 60)} minutes.`);
          } else {
            toast.error(result.error || "Login failed");
          }
          return;
        }

        if (result.user || result.session) {
          await logAuditEvent({
            action: "User logged in",
            category: "authentication",
            entityType: "user",
            entityId: result.user?.id || (result.session as { user?: { id: string } })?.user?.id || 'unknown',
            metadata: { email },
          });
          toast.success("Login successful!");
          
          // Preload the Dashboard chunk before navigating to prevent chunk load failures
          try {
            await import("./Dashboard");
          } catch (e) {
            console.warn("Dashboard preload failed, will retry on navigation:", e);
          }
          
          // Small delay to ensure session state propagates
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 100);
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Authentication failed";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Sign In — Microsports Session Management"
        description="Sign in to the Microsports admin platform to manage venues, sessions, attendance, timesheets and holidays."
        path="/auth"
      />
    <main className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-secondary/30 dark:from-background dark:via-background dark:to-card/50 p-4 py-8 relative">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.03),transparent_50%),radial-gradient(circle_at_70%_80%,hsl(var(--accent)/0.05),transparent_50%)] pointer-events-none" />
      
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      
      {/* Center content */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-10">
        {/* Logo */}
        <div className="mb-8">
          <img
            src="/logo_light.webp"
            alt="MicroSports"
            className="h-16 object-contain drop-shadow-sm block dark:hidden"
            width={238}
            height={64}
            fetchPriority="high"
          />
          <img
            src="/logo_dark.webp"
            alt="MicroSports"
            className="h-16 object-contain drop-shadow-md hidden dark:block"
            width={238}
            height={64}
          />
        </div>

        <Card className="w-full max-w-md shadow-lg dark:shadow-2xl border-border/50 dark:border-border bg-card/95 dark:bg-card/90 backdrop-blur-sm">
          <CardHeader className="space-y-1 pb-4">
            <h1 className="text-2xl font-bold text-center leading-none tracking-tight text-foreground">
              {isSignUp ? "Create your MicroSports account" : "Sign in to MicroSports"}
            </h1>

            <CardDescription className="text-center text-muted-foreground">
              {isSignUp
                ? "Sign up for Microsports Admin"
                : "Sign in to manage your sessions"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isRateLimited && lockoutTimeRemaining && lockoutTimeRemaining > 0 && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive dark:bg-destructive/20 dark:border-destructive/30">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <p className="text-sm">
                  Too many attempts. Please wait {formatTimeRemaining(lockoutTimeRemaining)} before trying again.
                </p>
              </div>
            )}
            <form onSubmit={handleAuth} className="space-y-4">
              {isSignUp && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-foreground font-medium">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isRateLimited && lockoutTimeRemaining !== null && lockoutTimeRemaining > 0}
                    className="bg-background/50 dark:bg-background/30 border-input focus:border-primary focus:ring-primary/20"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isRateLimited && lockoutTimeRemaining !== null && lockoutTimeRemaining > 0}
                  className="bg-background/50 dark:bg-background/30 border-input focus:border-primary focus:ring-primary/20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isRateLimited && lockoutTimeRemaining !== null && lockoutTimeRemaining > 0}
                  className="bg-background/50 dark:bg-background/30 border-input focus:border-primary focus:ring-primary/20"
                />
                {isSignUp && (
                  <p className="text-xs text-muted-foreground">
                    Min 10 characters, uppercase, lowercase, number & special character
                  </p>
                )}
              </div>
              <Button 
                type="submit" 
                className="w-full shadow-md hover:shadow-lg transition-shadow" 
                disabled={loading || (isRateLimited && lockoutTimeRemaining !== null && lockoutTimeRemaining > 0)}
              >
                {loading ? "Processing..." : isSignUp ? "Sign Up" : "Sign In"}
              </Button>
            </form>
            <div className="mt-6 space-y-3 text-center text-sm">
              {!isSignUp && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!email) {
                      toast.error("Please enter your email address first");
                      return;
                    }
                    try {
                      emailSchema.parse(email);
                    } catch {
                      toast.error("Please enter a valid email address");
                      return;
                    }
                    setLoading(true);
                    try {
                      const { data, error } = await client.functions.invoke("send-password-reset", {
                        body: { 
                          email, 
                          next: "/auth",
                        },
                      });
                      
                      if (error) {
                        toast.error(error.message || "Failed to send reset email");
                      } else if (data?.error) {
                        toast.error(data.error);
                      } else {
                        toast.success("Password reset email sent! Check your inbox.");
                      }
                    } catch (err) {
                      toast.error("Failed to send reset email. Please try again.");
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="text-muted-foreground hover:text-primary transition-colors"
                  disabled={loading}
                >
                  Forgot your password?
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsSignUp(!isSignUp)}
                className="block w-full text-primary font-medium hover:text-primary/80 transition-colors"
              >
                {isSignUp
                  ? "Already have an account? Sign in"
                  : "Need an account? Sign up"}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer Links */}
      <footer className="mt-8 flex justify-center gap-6 text-sm text-muted-foreground relative z-10">
        <Link to="/gdpr" className="hover:text-primary transition-colors">
          GDPR
        </Link>
        <Link to="/privacy-policy" className="hover:text-primary transition-colors">
          Privacy Policy
        </Link>
      </footer>
    </main>
    </>
  );
};

export default Auth;